import React, { useMemo, useState } from "react";
import * as HoverCard from "@radix-ui/react-hover-card";

import { useAppSelector } from "../../../app/store";
import { FormType } from "../../../api/formConfigs";
import { selectFormConfigById } from "../Logic/formConfigsSlice";

import ConfigBasedFormHoverCard from "./ConfigBasedFormHoverCard";
import ConfigBasedFormModal from "./ConfigBasedFormModal";

interface FormConfigCellProps {
  title: string;
  formType?: FormType;
  disabled?: boolean;
  values?: Record<string, any>;
  onChange(value: Record<string, any>): void;
}

const FormConfigCell: React.FC<FormConfigCellProps> = ({
  title,
  formType,
  disabled = false,
  values,
  onChange,
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);

  const formConfig = useAppSelector((state) =>
    formType ? selectFormConfigById(state, formType) : undefined
  );

  const localValues = useMemo<Record<string, any>>(
    () => (values as Record<string, any>) ?? {},
    [values]
  );

  const orderedFields = useMemo(() => {
    if (!formConfig?.configs?.fields) return [];
    const fields = formConfig.configs.fields;
    const leaderKeys = formConfig.configs.leader_field_keys ?? [];
    const leaderFields = fields.filter((field) => leaderKeys.includes(field.key));
    const remainingFields = fields.filter((field) => !leaderKeys.includes(field.key));
    return [...leaderFields, ...remainingFields];
  }, [formConfig]);

  const firstField = orderedFields[0];
  const firstValue = firstField ? localValues[firstField.key] : undefined;
  const additionalFieldsCount = Math.max(orderedFields.length - 1, 0);

  const displayValue = useMemo(() => {
    if (firstValue === undefined || firstValue === null || firstValue === "") {
      return "-";
    }

    if (firstField?.options) {
      const matchedOption = firstField.options.find(
        (opt) => String(opt.value) === String(firstValue)
      );
      if (matchedOption) return matchedOption.label;
    }

    if (typeof firstValue === "boolean") {
      return firstValue ? "Yes" : "No";
    }

    return String(firstValue);
  }, [firstValue, firstField]);


  const hasValues = useMemo(() => {
    return Object.values(localValues).some((val) => {
      if (Array.isArray(val)) return val.length > 0;
      return val !== undefined && val !== null && val !== "";
    });
  }, [localValues]);

  const handleSave = (updatedDetails: Record<string, any>) => {
    onChange(updatedDetails);
    setIsModalOpen(false);
  };

  if (disabled) {
    return (
      <button type="button" disabled className="py-1 text-sm font-medium text-[#A3A3A3] cursor-not-allowed select-none focus:outline-none">
        Add
      </button>
    );
  }

  if (!formType) {
    return <div className="px-2 py-1 text-sm text-[#A3A3A3] select-none">-</div>;
  }

  return (
    <>
      {!hasValues ? (
        <button type="button" onClick={() => setIsModalOpen(true)} className="py-1 text-sm font-normal text-[#0A0A0A]/60 transition-colors focus:outline-none">
          Add
        </button>
      ) : (
        <HoverCard.Root
          key={`${formType}-${JSON.stringify(localValues)}`}
          openDelay={0}
          closeDelay={0}
        >
          <HoverCard.Trigger asChild>
            <div className="flex items-center justify-between w-full cursor-pointer min-w-[220px] focus:outline-none">
              <div className="flex flex-col gap-1 min-w-0 items-start">
                <span className="text-[12px] font-normal leading-4 text-[#737373] truncate block w-full">{firstField?.label}</span>
                <span className="text-[14px] font-normal leading-5 text-[#0A0A0A] truncate block w-full">{displayValue}</span>
              </div>

              {additionalFieldsCount > 0 && (
                <div className="flex-shrink-0 rounded-[8px] bg-[#F3F4F6] px-2 py-0.5 text-[12px] font-medium leading-none text-[#4B5563]">
                  +{additionalFieldsCount}
                </div>
              )}
            </div>
          </HoverCard.Trigger>

          <HoverCard.Portal>
            <HoverCard.Content side="bottom" align="start" sideOffset={0} collisionPadding={16}>
              <ConfigBasedFormHoverCard
                formType={formType}
                values={values}
                onEdit={() => setIsModalOpen(true)}
              />
            </HoverCard.Content>
          </HoverCard.Portal>
        </HoverCard.Root>
      )}

      {isModalOpen && (
        <ConfigBasedFormModal title={title} formType={formType} values={values} onClose={() => setIsModalOpen(false)} onSave={handleSave} />
      )}
    </>
  );
};

export default FormConfigCell;